import { differenceInCalendarDays } from 'date-fns';

type CuotaDet = { cuota: number; fecha: string; monto: number; pagado: number; saldo: number; estado: string };

export type RegistroCobro = {
  vendedorId: string;
  vendedorNombre: string;
  comprador: string;
  lotes: string[];
  recibo: string;
  cuotas: { cuota: number; fecha: string; monto: number; pagado: number; saldo: number }[];
  cuotasTodas: CuotaDet[];
  totalMonto: number;
  totalPagado: number;
  totalSaldo: number;
  cuotasCount: number;
  fechaPago: string;
};

// Función robusta para obtener abonos sincronizados (idéntica a la usada en ReporteCobrosMensuales)
function getAbonosSincronizadosRobusto(lot: any, expenses: any[]): any[] {
  if (!lot?.payments) return [];
  return lot.payments.filter((p: any) => {
    if (p.type !== 'abono') return false;
    return expenses.some((e: any) => {
      let lotesExpense = e.lotes;
      if (typeof lotesExpense === 'string') lotesExpense = [lotesExpense];
      if (!Array.isArray(lotesExpense)) lotesExpense = [];
      const montoIgual = Math.abs(Number(e.monto) - Number(p.amount)) < 1;
      const tipoIngreso = e.tipo === 'ingreso';
      const fechaPago = new Date(p.date);
      const fechaExpense = new Date(e.fecha);
      const diffDias = Math.abs((fechaExpense.getTime() - fechaPago.getTime()) / (1000 * 60 * 60 * 24));
      const fechaIgual = diffDias <= 1;
      const referenciaIncluye = (e.referencia && (p.cuotaNumero ? e.referencia.includes(p.cuotaNumero) : false)) || (e.referencia && p.receipt && e.referencia.includes(p.receipt));
      const loteCoincide = lotesExpense.includes(lot.id?.toString());
      return montoIgual && tipoIngreso && fechaIgual && referenciaIncluye && loteCoincide;
    });
  });
}

const months = ['Todos','Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export function calcularCobrosDelMes(lots: any[], expenses: any[], sellers: any[], month: number, year: number): RegistroCobro[] {
  // month: 0-based (0=Enero)
  if (!lots || !expenses) return [];

  // Agrupar por grupo de venta (idéntico al componente)
  const grupos: Record<string, any[]> = {};
  lots.filter((l: any) => l.status === 'crédito' && l.paymentType === 'pagos').forEach((l: any) => {
    const key = `${l.seller}|${l.buyerName}|${l.buyerDPI}|${l.saleDate}|${l.receiptNumber}`;
    if (!grupos[key]) grupos[key] = [];
    grupos[key].push(l);
  });

  const selIdx = month; // 0-based index del mes solicitado
  const today = new Date();
  const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const result: RegistroCobro[] = [];

  Object.values(grupos).forEach((grupo) => {
    if (!grupo || grupo.length === 0) return;

    const comprador = grupo[0].buyerName;
    const vendedor = grupo[0].seller;
    const vendedorObj = sellers?.find((s: any) => s.id === vendedor || s.account === vendedor || s.name === vendedor);
    const vendedorNombre = vendedorObj ? vendedorObj.name : (vendedor || '-');
    const lotes = grupo.map((l: any) => l.id?.toString());
    const recibo = grupo[0].receiptNumber;
    const totalValue = grupo.reduce((sum: number, l: any) => sum + (Number(l.totalValue) || 0), 0);
    const payments = grupo.flatMap((l: any) => Array.isArray(l.payments) ? l.payments : []);
    const numberOfPayments = grupo[0].numberOfPayments || grupo[0].numeroPagos || 1;
    const enganche = payments.find((p: any) => p.type === 'enganche') ? Number(payments.find((p: any) => p.type === 'enganche').amount) : 0;
    const cuotaMensual = numberOfPayments > 0 ? (totalValue - enganche) / numberOfPayments : 0;
    const fechaVenta = new Date(grupo[0].saleDate || new Date());

    const abonosSincronizados = grupo.flatMap((l: any) => getAbonosSincronizadosRobusto(l, expenses));

    // Construir todas las cuotas del contrato
    const cuotasTodas: CuotaDet[] = [];
    for (let i = 1; i <= numberOfPayments; i++) {
      const fechaVenc = new Date(fechaVenta);
      fechaVenc.setMonth(fechaVenc.getMonth() + i);
      const fechaStr = fechaVenc.toISOString().split('T')[0];

      const pagosCuota = abonosSincronizados.filter((p: any) => {
        const sameCuota = String(p.cuotaNumero) === String(i);
        const pVenc = (p.cuotaFechaVencimiento || '').toString().slice(0, 10);
        const sameVenc = !p.cuotaFechaVencimiento || pVenc === fechaStr;
        return sameCuota && sameVenc;
      });
      const pagado = pagosCuota.reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
      const saldo = Math.max(cuotaMensual - pagado, 0);

      let estadoDet = 'Pendiente';
      if (saldo <= 0.0001) {
        estadoDet = 'Pagada';
      } else if (fechaVenc < todayMid) {
        estadoDet = 'Vencida';
      }
      cuotasTodas.push({ cuota: i, fecha: fechaStr, monto: cuotaMensual, pagado, saldo, estado: estadoDet });
    }

    // Marcar Próxima: primera cuota no pagada con fecha >= hoy
    let proximaAsignada = false;
    cuotasTodas.forEach(cuota => {
      if (!proximaAsignada && cuota.saldo > 0.0001 && new Date(cuota.fecha) >= todayMid) {
        cuota.estado = 'Próxima';
        proximaAsignada = true;
      }
    });

    // Filtrar cuotas para cobros del mes:
    // 1. Cuotas vencidas con saldo pendiente (cualquier fecha anterior)
    // 2. Cuotas del mes actual con saldo pendiente
    // NO incluir cuotas de meses futuros
    const cuotasDelMesActual = cuotasTodas.filter(cuota => {
      if (cuota.saldo <= 0.0001) return false; // Sin saldo pendiente
      
      const fechaVencimiento = new Date(cuota.fecha);
      const mesVencimiento = fechaVencimiento.getMonth();
      const añoVencimiento = fechaVencimiento.getFullYear();
      
      // Incluir si es del mes actual
      if (mesVencimiento === month && añoVencimiento === year) return true;
      
      // Incluir si es vencida (cualquier fecha anterior con saldo)
      if (cuota.estado === 'Vencida') return true;
      
      // NO incluir cuotas futuras (meses posteriores al actual)
      return false;
    });

    if (cuotasDelMesActual.length === 0) return;

    const totalMonto = cuotasDelMesActual.reduce((s, c) => s + c.monto, 0);
    const totalPagado = cuotasDelMesActual.reduce((s, c) => s + c.pagado, 0);
    const totalSaldo = cuotasDelMesActual.reduce((s, c) => s + c.saldo, 0);
    const cuotasCount = cuotasDelMesActual.length;

    // Fecha de pago (primera cuota del mes)
    const cuotaDelMes = cuotasTodas.find(c => (new Date(c.fecha)).getMonth() === month);
    const fechaPago = cuotaDelMes ? cuotaDelMes.fecha : '';

    result.push({
      vendedorId: vendedor,
      vendedorNombre,
      comprador,
      lotes,
      recibo,
      cuotas: cuotasDelMesActual.map(c => ({
        cuota: c.cuota,
        fecha: c.fecha,
        monto: c.monto,
        pagado: c.pagado,
        saldo: c.saldo,
        estado: c.estado
      })),
      cuotasTodas,
      totalMonto,
      totalPagado,
      totalSaldo,
      cuotasCount,
      fechaPago
    });
  });

  return result.sort((a, b) => a.vendedorNombre.localeCompare(b.vendedorNombre) || a.comprador.localeCompare(b.comprador));
}


